import React, { forwardRef, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PatientPicker from '@components/Patient/PatientPicker';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormHelperText, InputAdornment, TextField } from '@mui/material';

import type { Patient } from '../../../types/types';
import { type PaymentRequestFormData, paymentRequestSchema } from '../utils/validation';

import { PaymentInfoPopover } from './PaymentInfoText';

export interface PaymentRequestFormProps {
  // Initial data for editing
  defaultValues?: Partial<PaymentRequestFormData>;

  // Pre-filled/locked fields
  patientId?: string;
  patientName?: string;
  encounterId?: string;
  appointmentId?: string;
  actionId?: string;

  // Control which fields are shown
  showPatientPicker?: boolean;
  showDueDate?: boolean;

  // When true, reason becomes required and the field shows required-styling.
  // The backend always requires `reason`; the modal sets this when there's
  // no encounter/charge context to infer it from.
  requireReason?: boolean;

  // Form submission handler
  onSubmit: (data: PaymentRequestFormData) => void | Promise<void>;

  // Loading state from parent
  isLoading?: boolean;

  // Form ref for external access
  formRef?: React.RefObject<ReturnType<typeof useForm<PaymentRequestFormData>>>;
}

export const PaymentRequestForm = forwardRef<HTMLFormElement, PaymentRequestFormProps>(
  (
    {
      defaultValues,
      patientId,
      patientName: _patientName,
      encounterId,
      appointmentId,
      actionId,
      showPatientPicker = false,
      showDueDate = true,
      requireReason = false,
      onSubmit,
      isLoading,
      formRef,
    },
    ref,
  ) => {
    const form = useForm<PaymentRequestFormData>({
      resolver: zodResolver(paymentRequestSchema),
      defaultValues: {
        amount: '',
        patientId: patientId || '',
        reason: '',
        dueDate: '',
        appointmentId: appointmentId || '',
        encounterId: encounterId || '',
        actionId: actionId || '',
        ...defaultValues,
      },
    });

    // Expose form instance to parent if ref provided
    useEffect(() => {
      if (formRef && 'current' in formRef) {
        // @ts-expect-error - We're storing the form instance on the ref
        formRef.current = form;
      }
    }, [form, formRef]);

    // Update patientId when prop changes
    useEffect(() => {
      if (patientId) {
        form.setValue('patientId', patientId);
      }
    }, [patientId, form]);

    const handleSubmit = form.handleSubmit(data => {
      if (requireReason && !data.reason?.trim()) {
        form.setError('reason', { type: 'required', message: 'Reason is required' });
        return;
      }
      return onSubmit(data);
    });

    return (
      <form ref={ref} onSubmit={handleSubmit} id="payment-request-form">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showPatientPicker && !patientId && (
            <Box>
              <Controller
                name="patientId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <PatientPicker
                      onPatientSelect={(patient: Patient | undefined) => {
                        field.onChange(patient?.patient_id || '');
                      }}
                      initialPatient={undefined}
                      required
                    />
                    {fieldState.error && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}

          <Controller
            name="amount"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Amount"
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message || 'Enter the payment amount'}
                required
                fullWidth
                size="small"
                disabled={isLoading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
                onBlur={e => {
                  field.onBlur();
                  // Format to 2 decimal places
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    field.onChange(value.toFixed(2));
                  }
                }}
              />
            )}
          />

          <Controller
            name="reason"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={requireReason ? 'Reason' : 'Reason (Optional)'}
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  (requireReason
                    ? 'What is this charge for? Shown on the invoice.'
                    : 'Describe what this payment is for')
                }
                required={requireReason}
                fullWidth
                size="small"
                multiline
                rows={3}
                disabled={isLoading}
                inputProps={{
                  maxLength: 500,
                }}
              />
            )}
          />

          {showDueDate && (
            <Controller
              name="dueDate"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Due Date (Optional)"
                  type="date"
                  value={field.value || ''}
                  onChange={e => {
                    const value = e.target.value;
                    field.onChange(value);
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'When should this payment be made?'}
                  fullWidth
                  size="small"
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0],
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />
              )}
            />
          )}

          <PaymentInfoPopover />
        </Box>
      </form>
    );
  },
);

PaymentRequestForm.displayName = 'PaymentRequestForm';

export default PaymentRequestForm;
